import type { MultiAssetValueOverviewSummary, SingleAssetValueOverview } from '@einwert/valuation-api-client'

import { assembleAssetTitle, AssetList, ListCard, LoadingSkeleton, PageTemplate, tokens, useColors } from 'ComponentLibrary'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { Divider, styled, useTheme } from '@mui/material'
import { useSelectedAssets } from '../useSelectedAssets'
import { GeneralInfoCell } from './GeneralInfoCell'
import { ValueInfoCell } from 'ClientPortal/src/pages/Analysis/components/SelectableAssetList/ListItemCell/ValueInfoCell'
import { AssetInfoCell } from 'ClientPortal/src/pages/Analysis/components/SelectableAssetList/ListItemCell/AssetInfoCell'
import { useTranslation } from 'react-i18next'

type SelectableAssetListProps = {
    assets: SingleAssetValueOverview[]
    isPending?: boolean
    summary?: MultiAssetValueOverviewSummary
}

export function SelectableAssetList({ isPending = false, assets, summary }: SelectableAssetListProps) {
    const theme = useTheme()
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t } = useTranslation('pages', { keyPrefix: 'analysis.analysisPage' })
    const { selectedAssets, toggleAsset, getAssetColor } = useSelectedAssets()
    const { changeOpacity } = useColors()
    if (isPending) {
        return (
            <PageTemplate headline={{ title: tCommon('assets') }} withMargin fullWidth withBottomSpace withFooter>
                <LoadingSkeleton count={3} height="200px" />
            </PageTemplate>
        )
    }

    return (
        <AssetList aria-label={tCommon('assets')}>
            <ListItem
                color={changeOpacity(theme.palette.primary2.green100, 0.1)}
                shadedColor={changeOpacity(theme.palette.primary2.green100, 0.5)}
                highlighted={selectedAssets.length === 0}
            >
                <SummaryButton type="button" onClick={() => toggleAsset('summary')} aria-pressed={selectedAssets.length === 0}>
                    <GeneralCard>
                        <GeneralInfoCell
                            title={t('summarySeriesLabel')}
                            valueWithTrend={summary ? { value: summary.market_value.value, trend: summary.market_value.trend } : undefined}
                        />
                    </GeneralCard>
                </SummaryButton>
            </ListItem>
            <Divider />
            {assets.map(({ customer_asset_reference, address, uuid, title_image, valuation }, index) => {
                const selected = selectedAssets.includes(uuid)

                return (
                    <ListItem
                        key={uuid}
                        onClick={() => toggleAsset(uuid)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                toggleAsset(uuid)
                            }
                        }}
                        color={getAssetColor(index)}
                        shadedColor={getAssetColor(index, true)}
                        highlighted={selected}
                        tabIndex={-1}
                        style={{ marginBottom: theme.spacing(2) }}
                    >
                        <Checkbox checked={selected} ariaLabel={assembleAssetTitle({ ...address, customer_asset_reference })} />
                        <ListCard imageSrc={title_image}>
                            <AssetInfoCell address={address} customer_asset_reference={customer_asset_reference} />
                            <ValueInfoCell valuation={valuation} />
                        </ListCard>
                    </ListItem>
                )
            })}
        </AssetList>
    )
}

interface StyledAssetListProps {
    color?: string
    shadedColor?: string
    highlighted?: boolean
}

export const ListItem = styled('li', {
    // Configure which props should be forwarded on DOM
    shouldForwardProp: (prop) => prop !== 'color' && prop !== 'shadedColor' && prop !== 'highlighted' && prop !== 'sx',
    overridesResolver: (styles) => [styles.root],
})<StyledAssetListProps>(({ color, shadedColor, highlighted }) => {
    return {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: tokens.SizingSpacing100,
        borderRadius: '4px',
        backgroundColor: highlighted ? color : undefined,
        boxShadow: highlighted ? `0 0 0 8px ${color}` : undefined,
        '&:hover': {
            cursor: 'pointer',
            boxShadow: `0 0 0 8px ${shadedColor}`,
            backgroundColor: shadedColor,
        },
        '& > :last-child': {
            flex: 1,
            minWidth: 0,
        },
    }
})

const SummaryButton = styled('button')({
    width: '100%',
    padding: 0,
    border: 0,
    background: 'none',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'inherit',
    cursor: 'pointer',
})

const GeneralCard = styled(ListCard)({
    paddingLeft: '88px',
    alignItems: 'center',
})
